import { Component, OnDestroy, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { HeaderAppComponent } from '../../headers/header-app/header-app.component';
import { FormTemplateComponent } from '../../../shared/screen-templates/form-template/form-template.component';
import { PayeePasswordFormFieldComponent } from '../../../shared/payee-form-fields/payee-password-form-field/payee-password-form-field.component';
import { MatError } from '@angular/material/input';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-new-password',
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.scss'],
  imports: [
    HeaderAppComponent,
    FormTemplateComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeePasswordFormFieldComponent,
    MatError,
    PayeeButtonComponent,
    MatProgressSpinner,
    TranslateModule,
  ],
})
export class NewPasswordComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private readonly destroy = new Subject<void>();

  readonly passwordForm = this.fb.group({
    password: this.fb.control('', {
      validators: [Validators.required, Validators.minLength(6)],
      nonNullable: true,
    }),
    passwordConfirmation: this.fb.control('', {
      validators: [
        Validators.required,
        Validators.minLength(6),
        this.passwordConfirmationValidator.bind(this),
      ],
      nonNullable: true,
    }),
  });

  readonly email: string;
  readonly token: string;

  readonly loading = signal(false);

  constructor() {
    this.authService.logoutOnUnauthorized = false;
    this.email = this.route.snapshot.queryParams.email;
    this.token = this.route.snapshot.params.token;

    this.password.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: () => {
        this.passwordConfirmation.updateValueAndValidity();
      },
    });
  }

  get password() {
    return this.passwordForm.controls.password;
  }
  get passwordConfirmation() {
    return this.passwordForm.controls.passwordConfirmation;
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async setNewPassword(): Promise<void> {
    if (this.passwordForm.invalid) {
      this.password.markAsDirty();
      this.passwordConfirmation.markAsDirty();
      return;
    }

    try {
      this.loading.set(true);
      const value = this.passwordForm.getRawValue();

      await this.authService.resetPassword({
        token: this.token,
        password: value.password,
        password_confirmation: value.passwordConfirmation,
        email: this.email,
      });
    } catch (error) {
      console.error(error);
      this.snackBar.open(
        this.translate.instant('ERROR_MESSAGES.UNKNOWN_ERROR'),
        this.translate.instant('OK'),
        {
          duration: 5000,
        },
      );
    } finally {
      this.loading.set(false);
    }
  }

  private passwordConfirmationValidator(control: AbstractControl) {
    const value = control.value;
    if (!value) {
      return null;
    }
    if (value !== this.password.value) {
      return { passwordsDontMatch: true };
    }
    return null;
  }
}
